import { Component, computed, effect, inject, Signal } from '@angular/core'
import { ProductionTechnology } from '../../shared/models/production-technology.enum'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { GeneralFormControls, OfferFormService } from '../../shared/services/offer-form.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { DirectMarketingTooltipContentComponent } from '../direct-marketing-tooltip-content/direct-marketing-tooltip-content.component'
import { MatDialog } from '@angular/material/dialog'
import {
  BricksCardComponent,
  BricksDropdownComponent,
  BricksErrorComponent,
  BricksIconComponent,
  BricksInputComponent,
  BricksOptionComponent,
} from '@enbw_bricks/ui'
import { TooltipComponent } from '../../shared/components/tooltip/tooltip.component'
import { RequiredAsteriskComponent } from '../../shared/components/required-asterisk/required-asterisk.component'
import { CommonModule } from '@angular/common'
import moment from 'moment'
import { ContractDateService } from '../../shared/services/contract-date.service'
import { DateDayMonthYearPipe } from '../../shared/pipes/dateGermanFormat'
import { MARKETING_TYPE_OPTIONS, PRODUCTION_TECHNOLOGY_OPTIONS } from '../../constants'

@Component({
  selector: 'app-general-data',
  templateUrl: './general-data.component.html',
  styleUrls: ['./general-data.component.scss'],
  imports: [
    BricksInputComponent,
    BricksErrorComponent,
    BricksCardComponent,
    TooltipComponent,
    RequiredAsteriskComponent,
    BricksIconComponent,
    BricksDropdownComponent,
    BricksOptionComponent,
    ReactiveFormsModule,
    CommonModule,
    DateDayMonthYearPipe,
  ],
})
export class GeneralDataComponent {
  private readonly offerFormService = inject(OfferFormService)
  private readonly contractDateService = inject(ContractDateService)
  private readonly dialog = inject(MatDialog)

  protected readonly formGroup: FormGroup<GeneralFormControls> = this.offerFormService.generalData

  protected readonly isAlreadyInOperationOptions = [
    { value: true, label: 'Ja, die Anlage wurde bereits in Betrieb genommen.' },
    {
      value: false,
      label: 'Nein, die Anlage wurde noch nicht in Betrieb genommen.',
    },
  ]

  protected readonly validStartDates = computed(() => this.contractDateService.calculateValidStartDates())
  protected readonly validEndDates = computed(() =>
    this.contractDateService.calculateValidEndDates(this.contractStartDate(), this.isAlreadyInOperation())
  )
  private readonly contractEndDateEnabled = computed(() => this.isAlreadyInOperation() !== null)
  private readonly isAlreadyInOperation: Signal<boolean | null> = toSignal(
    this.formGroup.controls.isAlreadyInOperation.valueChanges,
    {
      initialValue: this.formGroup.controls.isAlreadyInOperation.value,
    }
  )
  private readonly productionTechnology: Signal<ProductionTechnology | null> = toSignal(
    this.formGroup.controls.productionTechnology.valueChanges,
    {
      initialValue: this.formGroup.controls.productionTechnology.value,
    }
  )
  private readonly contractStartDate: Signal<moment.Moment | null> = toSignal(
    this.formGroup.controls.contractStartDate.valueChanges,
    {
      initialValue: this.formGroup.controls.contractStartDate.value,
    }
  )

  constructor() {
    effect(() => {
      // Auto-select initial start date
      const startControl = this.formGroup.controls.contractStartDate
      if (this.validStartDates().length > 0 && !startControl.value) {
        startControl.setValue(this.validStartDates()[0])
      }
    })

    effect(() => {
      // Handle end date control and auto-selection
      const endDateControl = this.formGroup.controls.contractEndDate

      if (this.contractEndDateEnabled()) {
        endDateControl.enable()
        if (this.validEndDates().length > 0) {
          endDateControl.setValue(this.validEndDates()[0], { emitEvent: false })
        }
      } else {
        endDateControl.disable()
      }
    })

    effect(() => {
      if (this.productionTechnology()) {
        this.formGroup.controls.productionTechnology.disable()
      }
    })
  }

  openOverlay() {
    this.dialog.open(DirectMarketingTooltipContentComponent, { width: '640px' })
  }

  protected readonly PRODUCTION_TECHNOLOGY_OPTIONS = PRODUCTION_TECHNOLOGY_OPTIONS
  protected readonly MARKETING_TYPE_OPTIONS = MARKETING_TYPE_OPTIONS
}
